/** getState() 并发合并 + 短 TTL 缓存（供 basic / 写操作后失效） */

export type MouseGetStateResult = {
  code: number;
  message: string;
  data: any;
};

export const mouseGetStateCache = {
  inFlight: null as Promise<MouseGetStateResult> | null,
  /** 仅当前代 inFlight 可在 finally 里清空，避免旧请求踩掉新请求 */
  inFlightGen: 0,
  /** 与 inFlight / lastOk 绑定的设备，避免 A/C 双鼠共用短缓存 */
  inFlightKey: "" as string,
  lastOk: null as MouseGetStateResult | null,
  lastOkAt: 0,
  lastOkKey: "" as string,
  okTtlMs: 500,
  writeRev: 0,
  /** 最近一次写成功时间；此窗口内 HID 回读可能仍是旧键 */
  lastWriteAt: 0,
};

/** 展立/富芮坤写后立刻 getState 会读到旧键 */
export const KEY_REMAP_SKIP_HID_SOLUTIONS = new Set(["zhanli", "freq"]);

export const KEY_REMAP_HID_GRACE_MS = 8000;

export function shouldKeepLastKeysAfterWrite(): boolean {
  return (
    Date.now() - (mouseGetStateCache.lastWriteAt || 0) < KEY_REMAP_HID_GRACE_MS
  );
}

/** 展立/富芮坤写成功后勿立刻 getState（固件未稳会读回旧键） */
export function skipHidReadAfterWriteOpts():
  | { dispatchSyncEvent: false }
  | undefined {
  if (typeof localStorage === "undefined") return undefined;
  const solution = localStorage.getItem("solutionRoviderName");
  return solution && KEY_REMAP_SKIP_HID_SOLUTIONS.has(solution)
    ? { dispatchSyncEvent: false }
    : undefined;
}

function readMouseGetStateCacheKey(): string {
  if (typeof window === "undefined") return "";
  try {
    return (
      localStorage.getItem("mouseCurrentStableId") ||
      localStorage.getItem("currentDeviceId") ||
      ""
    );
  } catch {
    return "";
  }
}

export { readMouseGetStateCacheKey };

/**
 * 失效成功缓存。不清 inFlight：进行中的请求用 writeRev/generation 自行作废，
 * 避免 clear 把后续合并进来的 inFlight 直接置空导致第三路并发读。
 */
export function clearStateCache(): void {
  mouseGetStateCache.lastOk = null;
  mouseGetStateCache.lastOkAt = 0;
  mouseGetStateCache.lastOkKey = "";
  try {
    // 与 clearRememberedMouseDeviceInfo 对齐：切设备后勿让子应用吃到上一台 LAST
    const g = globalThis as unknown as {
      __MOUSE_DEVICE_INFO_LAST__?: unknown;
      __MOUSE_DEVICE_INFO_SEQ__?: number;
    };
    g.__MOUSE_DEVICE_INFO_LAST__ = undefined;
    g.__MOUSE_DEVICE_INFO_SEQ__ =
      (typeof g.__MOUSE_DEVICE_INFO_SEQ__ === "number"
        ? g.__MOUSE_DEVICE_INFO_SEQ__
        : 0) + 1;
  } catch {
    /* ignore */
  }
}

export function markWrite(): number {
  mouseGetStateCache.writeRev += 1;
  mouseGetStateCache.lastWriteAt = Date.now();
  return mouseGetStateCache.writeRev;
}

export function syncOnOk<T extends { code?: number }>(
  res: T,
  merge?: () => void,
  options?: { dispatchSyncEvent?: boolean }
): T {
  if (!res || res.code !== 200) return res;
  if (typeof merge === "function") {
    try {
      merge();
    } catch {
      /* ignore */
    }
  }
  markWrite();
  clearStateCache();
  const shouldDispatch = options?.dispatchSyncEvent !== false;
  if (shouldDispatch && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("mouseDeviceNeedSync"));
  }
  return res;
}
